import { BaseService } from './base.service';
import { CustomResponseEntity } from '../types/common';
import { ProgramVersionItemDto } from '../types/dtos/program';
import { api } from '../client/axiosClient';
import { PaginationParams } from '../types/common';

/**
 * ProgramVersionItemService handles CRUD operations for program version items.
 *
 * URL Pattern: /api/v1/programs/{programId}/versions/{versionId}/items
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * VAL-PRG-005: ProgramVersionItem List with X-Organization-Id Header
 *
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param programId - Parent program UUID for nested resource path
 * @param versionId - Parent version UUID for nested resource path
 */
export class ProgramVersionItemService extends BaseService<ProgramVersionItemDto> {
  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param programId - Parent program UUID for nested resource path
   * @param versionId - Parent version UUID for nested resource path
   */
  constructor(_organizationId: string, programId: string, versionId: string) {
    super(`/api/v1/programs/${programId}/versions/${versionId}/items`);
  }

  findAll(params?: PaginationParams & { isActive?: boolean }): Promise<CustomResponseEntity<ProgramVersionItemDto[]>> {
    return api.get(this.baseUrl, { params });
  }

  findById(id: string): Promise<CustomResponseEntity<ProgramVersionItemDto>> {
    return api.get(`${this.baseUrl}/${id}`);
  }

  create(item: ProgramVersionItemDto): Promise<CustomResponseEntity<ProgramVersionItemDto>> {
    return api.post(this.baseUrl, item);
  }

  update(id: string, item: ProgramVersionItemDto): Promise<CustomResponseEntity<ProgramVersionItemDto>> {
    return api.put(`${this.baseUrl}/${id}`, item);
  }

  delete(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(`${this.baseUrl}/${id}`);
  }
}

/**
 * Factory function to create a ProgramVersionItemService instance
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param programId - Parent program UUID for nested resource path
 * @param versionId - Parent version UUID for nested resource path
 */
export const createProgramVersionItemService = (
  _organizationId: string,
  programId: string,
  versionId: string
) => new ProgramVersionItemService(_organizationId, programId, versionId); 